// src/components/map/CustomPointStyleEditor.jsx
import React, { useState, useEffect, useCallback } from 'react';
import chroma from 'chroma-js'; // For color manipulation if needed
import ColorPicker from './ColorPicker';
import { getCompFieldFormat } from './heatMapGenerator';

// --- Re-include or import helper functions ---
// These would typically be in a shared utils file

/**
 * TCG Color palette with precise RGB values
 */
const TCG_COLORS = {
  'TCG Red Dark': [191, 0, 0],
  'TCG Orange Dark': [255, 122, 13],
  'TCG Yellow Dark': [248, 242, 0],
  'TCG Green Dark': [0, 191, 44],
  'TCG Cyan Dark': [0, 155, 155],
  'TCG Blue Dark': [0, 51, 128],
  'TCG Purple Dark': [92, 0, 184],
  'Pink Dark': [214, 0, 158],
  'Brown Dark': [148, 112, 60],
  'Carbon Gray Light': [174, 170, 170]
};

/**
 * Gets the appropriate TCG color for each break based on the
 * break index and total number of breaks.
 */
const getBreakColor = (index, totalBreaks) => {
  // Complete color mappings for all break levels (1-10)
  // IMPORTANT: Colors go from Purple/Blue (lowest values) to Red (highest values)
  // Red is ALWAYS the last/highest value - no Pink/Brown/Grey colors used
  const colorMappings = {
    1: ['TCG Purple Dark'],
    2: ['TCG Purple Dark', 'TCG Red Dark'],
    3: ['TCG Purple Dark', 'TCG Green Dark', 'TCG Red Dark'],
    4: ['TCG Purple Dark', 'TCG Cyan Dark', 'TCG Yellow Dark', 'TCG Red Dark'],
    5: ['TCG Purple Dark', 'TCG Blue Dark', 'TCG Green Dark', 'TCG Orange Dark', 'TCG Red Dark'],
    6: ['TCG Purple Dark', 'TCG Blue Dark', 'TCG Cyan Dark', 'TCG Yellow Dark', 'TCG Orange Dark', 'TCG Red Dark'],
    7: ['TCG Purple Dark', 'TCG Blue Dark', 'TCG Cyan Dark', 'TCG Green Dark', 'TCG Yellow Dark', 'TCG Orange Dark', 'TCG Red Dark'],
    8: ['TCG Purple Dark', 'TCG Purple Dark', 'TCG Blue Dark', 'TCG Cyan Dark', 'TCG Green Dark', 'TCG Yellow Dark', 'TCG Orange Dark', 'TCG Red Dark'],
    9: ['TCG Purple Dark', 'TCG Purple Dark', 'TCG Blue Dark', 'TCG Cyan Dark', 'TCG Green Dark', 'TCG Yellow Dark', 'TCG Orange Dark', 'TCG Orange Dark', 'TCG Red Dark'],
    10: ['TCG Purple Dark', 'TCG Purple Dark', 'TCG Blue Dark', 'TCG Blue Dark', 'TCG Cyan Dark', 'TCG Green Dark', 'TCG Yellow Dark', 'TCG Orange Dark', 'TCG Orange Dark', 'TCG Red Dark']
  };

  // Validate inputs and get appropriate color mapping
  const validBreakCount = Math.max(1, Math.min(10, Math.floor(totalBreaks)));
  const validIndex = Math.max(0, Math.min(validBreakCount - 1, index));
  const colorNames = colorMappings[validBreakCount] || colorMappings[10];
  const colorName = colorNames[validIndex];

  // Convert RGB array to RGBA array format
  const rgbValues = TCG_COLORS[colorName];
  const alpha = 0.8; // Default alpha value

  return [rgbValues[0], rgbValues[1], rgbValues[2], alpha];
};

/**
 * Convert color to CSS string format
 * Handles: hex strings, rgba arrays, rgb arrays, named colors
 */
const colorToCss = (color, defaultColor = '#FF0000') => {
  if (!color) return defaultColor;
  if (typeof color === 'string') return color;
  if (Array.isArray(color) && color.length >= 3) {
    const [r, g, b, a = 1] = color;
    return `rgba(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)}, ${a})`;
  }
  return defaultColor;
};

/**
 * Convert color to hex format for color input
 * Color inputs only accept #RRGGBB format
 */
const colorToHex = (color, defaultColor = '#FF0000') => {
  if (!color) return defaultColor;
  if (typeof color === 'string') {
    // If it's already a hex color, return it
    if (color.startsWith('#')) return color;
    // If it's rgba/rgb, try to parse with chroma
    try {
      return chroma(color).hex();
    } catch {
      return defaultColor;
    }
  }
  if (Array.isArray(color) && color.length >= 3) {
    const [r, g, b] = color;
    return chroma.rgb(r, g, b).hex();
  }
  return defaultColor;
};

/**
 * Formats a numeric value using the valueFormat settings (prefix, suffix, decimals)
 * Used to create properly formatted legend labels
 *
 * @param {number} value - The numeric value to format
 * @param {Object} valueFormat - Format object with prefix, suffix, decimals, noCommas, isYear properties
 * @param {boolean} includeSuffix - Whether to include the suffix (default true)
 * @returns {string} - Formatted value string
 */
const formatValueWithFormat = (value, valueFormat, includeSuffix = true) => {
  if (value === undefined || value === null || typeof value !== 'number' || isNaN(value)) {
    return String(value ?? '');
  }

  const format = valueFormat || {};
  const prefix = format.prefix || '';
  const suffix = format.suffix || '';
  const decimals = format.decimals !== undefined ? format.decimals : 0;
  const noCommas = format.noCommas || false;

  let formattedValue;
  if (decimals === 0) {
    formattedValue = Math.round(value);
    if (!noCommas) {
      formattedValue = formattedValue.toLocaleString();
    }
  } else {
    formattedValue = value.toFixed(decimals);
    if (!noCommas) {
      // toFixed returns string, need to convert to number for toLocaleString
      const parts = formattedValue.split('.');
      parts[0] = parseInt(parts[0], 10).toLocaleString();
      formattedValue = parts.join('.');
    }
  }

  const suffixPart = includeSuffix && suffix ? suffix : '';
  return `${prefix}${formattedValue}${suffixPart}`;
};

const smartRound = (value, dataRange = null) => {
  if (typeof value !== 'number' || isNaN(value) || value === Infinity) return value;

  // If dataRange is provided, use it to determine appropriate rounding precision
  // This prevents over-rounding when the data range is small relative to the values
  if (dataRange !== null && dataRange > 0) {
    // Round to about 1% of the range for good granularity
    const precision = Math.pow(10, Math.floor(Math.log10(dataRange)) - 1);
    if (precision >= 1) {
      return Math.round(value / precision) * precision;
    }
    return Math.round(value * 100) / 100; // 2 decimal places for small ranges
  }

  // Default behavior when no range provided
  if (Math.abs(value) < 10) return Math.round(value);
  if (Math.abs(value) < 100) return Math.round(value / 10) * 10;
  if (Math.abs(value) < 1000) return Math.round(value / 100) * 100;
  if (Math.abs(value) < 10000) return Math.round(value / 1000) * 1000;
  return Math.round(value / 10000) * 10000;
};

const generateColorRamp = (color1, color2, count) => {
  const validCount = Math.max(1, count);
  if (validCount === 1) return [color1];
  return chroma.scale([color1, color2]).mode('lch').colors(validCount);
};

/**
 * Check if a column contains mostly text/categorical data rather than numeric data
 * Returns true if more than 50% of non-empty values are non-numeric
 */
const isTextColumn = (data, valueColumn) => {
  if (!data || data.length === 0 || !valueColumn) return false;

  const values = data
    .map(item => item[valueColumn])
    .filter(val => val !== null && val !== undefined && val !== '');

  if (values.length === 0) return false;

  const numericCount = values.filter(val => !isNaN(Number(val))).length;
  const textRatio = (values.length - numericCount) / values.length;

  // If more than 50% are non-numeric, treat as text column
  return textRatio > 0.5;
};

/**
 * Generate breaks for text/categorical columns
 * Each unique value gets its own color
 */
const generateTextBreaksForPoints = (data, valueColumn, baseSymbolStyle = {}) => {
  if (!data || data.length === 0 || !valueColumn) return null;

  // Extract unique text values
  const uniqueValues = [...new Set(
    data
      .map(item => item[valueColumn])
      .filter(val => val !== null && val !== undefined && val !== '')
      .map(val => String(val))
  )].sort();

  if (uniqueValues.length === 0) {
    console.warn(`[generateTextBreaksForPoints] No valid text data in '${valueColumn}'`);
    return null;
  }

  console.log(`[generateTextBreaksForPoints] Found ${uniqueValues.length} unique values for "${valueColumn}"`);

  // Limit to 10 unique values max (using first 10 colors in palette)
  const maxBreaks = Math.min(uniqueValues.length, 10);
  const valuesToUse = uniqueValues.slice(0, maxBreaks);

  // Create a break for each unique value
  const breaks = valuesToUse.map((value, index) => ({
    value: value, // Store the actual text value for matching
    minValue: index, // Use index for ordering
    maxValue: index,
    isTextField: true, // Flag to indicate this is a text field break
    label: value, // Just the text value
    symbol: { ...baseSymbolStyle, color: getBreakColor(index, maxBreaks) }
  }));

  console.log(`[generateTextBreaksForPoints] Generated ${breaks.length} text breaks for "${valueColumn}"`);
  return breaks;
};

const generateClassBreaksForPoints = (data, valueColumn, numClasses = 5, baseSymbolStyle = {}, valueFormat = null) => {
  if (!data || data.length === 0 || !valueColumn) return null;

  // Check if this is a text column - use text breaks instead
  if (isTextColumn(data, valueColumn)) {
    console.log(`[generateClassBreaksForPoints] "${valueColumn}" detected as text column, using text breaks`);
    return generateTextBreaksForPoints(data, valueColumn, baseSymbolStyle);
  }

  const values = data.map(item => Number(item[valueColumn])).filter(val => !isNaN(val));
  if (values.length < 2) return null;

  // Auto-detect valueFormat if not provided
  const effectiveFormat = valueFormat || getCompFieldFormat(valueColumn);
  console.log(`[CustomPointStyleEditor] Using valueFormat for '${valueColumn}':`, effectiveFormat);

  const sortedValues = [...values].sort((a, b) => a - b);
  const minValue = sortedValues[0];
  const maxValue = sortedValues[sortedValues.length - 1];
  const dataRange = maxValue - minValue;
  const roundedMin = smartRound(minValue, dataRange);
  const roundedMax = smartRound(maxValue, dataRange);

  if (minValue === maxValue) {
    const formattedValue = formatValueWithFormat(roundedMin, effectiveFormat, true);
    return [{
      minValue: roundedMin, maxValue: roundedMin, label: formattedValue,
      symbol: { ...baseSymbolStyle, color: getBreakColor(0, 1) }
    }];
  }

  // Use quantile breaks - distribute data points evenly across breaks
  const validNumClasses = Math.max(1, Math.min(numClasses, values.length));
  const dataLength = sortedValues.length;
  const step = dataLength / validNumClasses;
  const breaks = [];
  const colors = Array.from({length: validNumClasses}, (_, i) => getBreakColor(i, validNumClasses));

  for (let i = 0; i < validNumClasses; i++) {
    const startIdx = Math.floor(i * step);
    const endIdx = (i === validNumClasses - 1) ? dataLength - 1 : Math.floor((i + 1) * step) - 1;

    const rangeMin = sortedValues[startIdx];
    const rangeMax = sortedValues[endIdx];
    const roundedClassMin = smartRound(rangeMin, dataRange);
    const roundedClassMax = smartRound(rangeMax, dataRange);

    // Format labels with proper prefix/suffix
    const formattedMin = formatValueWithFormat(roundedClassMin, effectiveFormat, false);
    const formattedMax = formatValueWithFormat(roundedClassMax, effectiveFormat, true);
    // Only show range if min and max are different, otherwise just show single value
    const label = formattedMin === formattedMax ? formattedMin : `${formattedMin} - ${formattedMax}`;

    breaks.push({
      minValue: roundedClassMin,
      maxValue: roundedClassMax,
      label: label,
      symbol: { ...baseSymbolStyle, color: colors[i] }
    });
  }

  // Fix gaps and overlaps: ensure breaks are continuous with no gaps
  // Each break's maxValue should equal the next break's minValue
  for (let i = 0; i < breaks.length - 1; i++) {
    // Set the next break's minValue to current break's maxValue to ensure continuity
    // The renderer uses minValue < value <= maxValue, so this creates no gaps
    breaks[i + 1].minValue = breaks[i].maxValue;

    // Update label to reflect the adjusted range with proper formatting
    const formattedMin = formatValueWithFormat(breaks[i + 1].minValue, effectiveFormat, false);
    const formattedMax = formatValueWithFormat(breaks[i + 1].maxValue, effectiveFormat, true);
    // Only show range if min and max are different, otherwise just show single value
    breaks[i + 1].label = formattedMin === formattedMax ? formattedMin : `${formattedMin} - ${formattedMax}`;
  }

  // Ensure first break starts at or below the minimum value
  if (breaks.length > 0 && breaks[0].minValue > roundedMin) {
    breaks[0].minValue = roundedMin;
    const formattedMin = formatValueWithFormat(breaks[0].minValue, effectiveFormat, false);
    const formattedMax = formatValueWithFormat(breaks[0].maxValue, effectiveFormat, true);
    breaks[0].label = `${formattedMin} - ${formattedMax}`;
  }

  // Ensure the last break includes the maximum value exactly
  if (breaks.length > 0) {
    breaks[breaks.length - 1].maxValue = roundedMax;
    const formattedMin = formatValueWithFormat(breaks[breaks.length - 1].minValue, effectiveFormat, false);
    const formattedMax = formatValueWithFormat(breaks[breaks.length - 1].maxValue, effectiveFormat, true);
    breaks[breaks.length - 1].label = `${formattedMin} - ${formattedMax}`;
  }

  return breaks;
};

const generateSizeBreaksForPoints = (data, valueColumn, numClasses = 5, minSize = 6, maxSize = 24, valueFormat = null) => {
  if (!data || data.length === 0 || !valueColumn) return null;
  const values = data.map(item => Number(item[valueColumn])).filter(val => !isNaN(val));
  if (values.length < 2) return null;

  // Auto-detect valueFormat if not provided
  const effectiveFormat = valueFormat || getCompFieldFormat(valueColumn);

  const sortedValues = [...values].sort((a, b) => a - b);
  const minValue = sortedValues[0];
  const maxValue = sortedValues[sortedValues.length - 1];
  const dataRange = maxValue - minValue;
  const roundedMin = smartRound(minValue, dataRange);
  const roundedMax = smartRound(maxValue, dataRange);

  if (minValue === maxValue) {
    const formattedValue = formatValueWithFormat(roundedMin, effectiveFormat, true);
    return [{ minValue: roundedMin, maxValue: roundedMin, label: formattedValue, size: Math.round((minSize + maxSize) / 2) }];
  }

  // Use quantile breaks for even distribution
  const validNumClasses = Math.max(1, Math.min(numClasses, values.length));
  const dataLength = sortedValues.length;
  const step = dataLength / validNumClasses;
  const sizeRange = maxSize - minSize;
  const breaks = [];

  for (let i = 0; i < validNumClasses; i++) {
    const startIdx = Math.floor(i * step);
    const endIdx = (i === validNumClasses - 1) ? dataLength - 1 : Math.floor((i + 1) * step) - 1;

    const rangeMin = sortedValues[startIdx];
    const rangeMax = sortedValues[endIdx];
    const roundedClassMin = smartRound(rangeMin, dataRange);
    const roundedClassMax = smartRound(rangeMax, dataRange);

    const proportion = (i + 0.5) / validNumClasses;
    const currentSize = minSize + (proportion * sizeRange);

    // Format labels with proper prefix/suffix
    const formattedMin = formatValueWithFormat(roundedClassMin, effectiveFormat, false);
    const formattedMax = formatValueWithFormat(roundedClassMax, effectiveFormat, true);
    // Only show range if min and max are different, otherwise just show single value
    const label = formattedMin === formattedMax ? formattedMin : `${formattedMin} - ${formattedMax}`;

    breaks.push({
      minValue: roundedClassMin,
      maxValue: roundedClassMax,
      label: label,
      size: Math.round(currentSize)
    });
  }

  // Fix gaps and overlaps: ensure breaks are continuous with no gaps
  // Each break's maxValue should equal the next break's minValue
  for (let i = 0; i < breaks.length - 1; i++) {
    // Set the next break's minValue to current break's maxValue to ensure continuity
    // The renderer uses minValue < value <= maxValue, so this creates no gaps
    breaks[i + 1].minValue = breaks[i].maxValue;

    // Update label to reflect the adjusted range with proper formatting
    const formattedMin = formatValueWithFormat(breaks[i + 1].minValue, effectiveFormat, false);
    const formattedMax = formatValueWithFormat(breaks[i + 1].maxValue, effectiveFormat, true);
    // Only show range if min and max are different, otherwise just show single value
    breaks[i + 1].label = formattedMin === formattedMax ? formattedMin : `${formattedMin} - ${formattedMax}`;
  }

  // Ensure first break starts at or below the minimum value
  if (breaks.length > 0 && breaks[0].minValue > roundedMin) {
    breaks[0].minValue = roundedMin;
    const formattedMin = formatValueWithFormat(breaks[0].minValue, effectiveFormat, false);
    const formattedMax = formatValueWithFormat(breaks[0].maxValue, effectiveFormat, true);
    breaks[0].label = `${formattedMin} - ${formattedMax}`;
  }

  // Ensure the last break includes the maximum value exactly
  if (breaks.length > 0) {
    breaks[breaks.length - 1].maxValue = roundedMax;
    const formattedMin = formatValueWithFormat(breaks[breaks.length - 1].minValue, effectiveFormat, false);
    const formattedMax = formatValueWithFormat(breaks[breaks.length - 1].maxValue, effectiveFormat, true);
    breaks[breaks.length - 1].label = `${formattedMin} - ${formattedMax}`;
  }

  return breaks;
};
// --- End Helper Functions ---


const SymbolPreview = ({ symbol }) => {
  if (!symbol) return null;
  const style = {
    width: `${symbol.size || 10}px`,
    height: `${symbol.size || 10}px`,
    backgroundColor: colorToCss(symbol.color, '#FF0000'),
    borderRadius: symbol.style === 'circle' ? '50%' : '0',
    border: `${symbol.outline?.width || 1}px solid ${colorToCss(symbol.outline?.color, '#FFFFFF')}`,
    display: 'inline-block',
    marginRight: '8px',
  };
  return <span style={style}></span>;
};


const CustomPointStyleEditor = ({ config, onChange, onPreview, valueFormats }) => {
  const [localConfig, setLocalConfig] = useState(config);
  const [numColorClasses, setNumColorClasses] = useState(config?.colorClassBreakInfos?.length || 5);
  // Handle both sizeInfos and sizeClassBreakInfos
  const [numSizeClasses, setNumSizeClasses] = useState((config?.sizeClassBreakInfos?.length || config?.sizeInfos?.length) || 5);
  const [minSymSize, setMinSymSize] = useState(config?.symbol?.minSize || 6); // For size ramp
  const [maxSymSize, setMaxSymSize] = useState(config?.symbol?.maxSize || 24); // For size ramp
  const [dotTransparency, setDotTransparency] = useState(config?.dotTransparency !== undefined ? config.dotTransparency : 1); // Transparency for all dots (0-1)
  const [dotTransparencyInput, setDotTransparencyInput] = useState(config?.dotTransparency !== undefined ? config.dotTransparency : 1); // For debounced slider

  // Local state for debounced input values (allows typing multi-digit numbers)
  const [minSymSizeInput, setMinSymSizeInput] = useState(config?.symbol?.minSize || 6);
  const [maxSymSizeInput, setMaxSymSizeInput] = useState(config?.symbol?.maxSize || 24);

  // State for pending value column changes - requires legend title before applying
  const [pendingColorColumn, setPendingColorColumn] = useState(null);
  const [pendingColorLegendTitle, setPendingColorLegendTitle] = useState('');
  const [pendingSizeColumn, setPendingSizeColumn] = useState(null);
  const [pendingSizeLegendTitle, setPendingSizeLegendTitle] = useState('');

  // Debounce effect for min size input
  useEffect(() => {
    const timer = setTimeout(() => {
      if (minSymSizeInput !== minSymSize) {
        const val = parseInt(minSymSizeInput, 10);
        let newMin = Math.max(1, val);
        if (newMin >= maxSymSize) {
          newMin = maxSymSize - 1 >= 1 ? maxSymSize - 1 : 1;
        }
        setMinSymSize(newMin);

        const updatedSymbol = {
          ...(localConfig.symbol || {}),
          minSize: newMin,
          maxSize: maxSymSize,
        };
        const newConfig = {
          ...localConfig,
          symbol: updatedSymbol,
        };
        handleFullConfigChange(newConfig);
      }
    }, 100); // 100ms delay for faster real-time feedback

    return () => clearTimeout(timer);
  }, [minSymSizeInput]);

  // Debounce effect for max size input
  useEffect(() => {
    const timer = setTimeout(() => {
      if (maxSymSizeInput !== maxSymSize) {
        const val = parseInt(maxSymSizeInput, 10);
        let newMax = Math.max(1, val);
        if (newMax <= minSymSize) {
          newMax = minSymSize + 1;
        }
        setMaxSymSize(newMax);

        const updatedSymbol = {
          ...(localConfig.symbol || {}),
          minSize: minSymSize,
          maxSize: newMax,
        };
        const newConfig = {
          ...localConfig,
          symbol: updatedSymbol,
        };
        handleFullConfigChange(newConfig);
      }
    }, 100); // 100ms delay for faster real-time feedback

    return () => clearTimeout(timer);
  }, [maxSymSizeInput]);

  // Debounce effect for transparency input
  useEffect(() => {
    const timer = setTimeout(() => {
      if (dotTransparencyInput !== dotTransparency) {
        setDotTransparency(dotTransparencyInput);
        const newConfig = { ...localConfig, dotTransparency: dotTransparencyInput };
        handleFullConfigChange(newConfig);
      }
    }, 300); // 300ms delay for smoother sliding

    return () => clearTimeout(timer);
  }, [dotTransparencyInput]);

  useEffect(() => {
    // Deep copy config to avoid direct mutation issues if parent re-renders
    const deepConfig = JSON.parse(JSON.stringify(config));
    // Normalize: if we have sizeClassBreakInfos but no sizeInfos, use sizeClassBreakInfos
    if (deepConfig.sizeClassBreakInfos && !deepConfig.sizeInfos) {
      deepConfig.sizeInfos = deepConfig.sizeClassBreakInfos;
    }
    // Also normalize sizeClassBreakInfos if we only have sizeInfos
    if (deepConfig.sizeInfos && !deepConfig.sizeClassBreakInfos) {
      deepConfig.sizeClassBreakInfos = deepConfig.sizeInfos;
    }

    console.log('[CustomPointStyleEditor] Config loaded:', {
      type: deepConfig.type,
      rendererType: deepConfig.rendererType,
      hasValueColumn1: !!deepConfig.valueColumn1,
      hasValueColumn2: !!deepConfig.valueColumn2,
      hasColorBreaks: !!deepConfig.colorClassBreakInfos?.length,
      hasSizeInfos: !!deepConfig.sizeInfos?.length,
      hasSizeClassBreakInfos: !!deepConfig.sizeClassBreakInfos?.length,
      minSize: deepConfig.symbol?.minSize,
      maxSize: deepConfig.symbol?.maxSize,
      hasCustomData: !!deepConfig.customData?.data?.length
    });

    setLocalConfig(deepConfig);
    setNumColorClasses(deepConfig?.colorClassBreakInfos?.length || 5);
    setNumSizeClasses((deepConfig?.sizeClassBreakInfos?.length || deepConfig?.sizeInfos?.length) || 5);
    setMinSymSize(deepConfig?.symbol?.minSize || 6);
    setMaxSymSize(deepConfig?.symbol?.maxSize || 24);
    setMinSymSizeInput(deepConfig?.symbol?.minSize || 6);
    setMaxSymSizeInput(deepConfig?.symbol?.maxSize || 24);
    const newTransparency = deepConfig?.dotTransparency !== undefined ? deepConfig.dotTransparency : 1;
    setDotTransparency(newTransparency);
    setDotTransparencyInput(newTransparency);
  }, [config]);

  const handleFullConfigChange = useCallback((newConfig) => {
    // Ensure critical properties are preserved
    const preservedConfig = {
      ...newConfig,
      // Preserve essential properties that may be missing from newConfig
      type: newConfig.type || localConfig.type || 'custom',
      rendererType: newConfig.rendererType || localConfig.rendererType || 'custom-dual-value',
      customData: newConfig.customData || localConfig.customData,
      // Preserve value columns (critical for dual-value rendering)
      valueColumn1: newConfig.valueColumn1 !== undefined ? newConfig.valueColumn1 : localConfig.valueColumn1,
      valueColumn2: newConfig.valueColumn2 !== undefined ? newConfig.valueColumn2 : localConfig.valueColumn2,
      // Preserve rendering breaks (critical for visualization)
      colorClassBreakInfos: newConfig.colorClassBreakInfos || localConfig.colorClassBreakInfos,
      sizeInfos: newConfig.sizeInfos || localConfig.sizeInfos,
      sizeClassBreakInfos: newConfig.sizeClassBreakInfos || localConfig.sizeClassBreakInfos,
      // Preserve location columns
      latitudeColumn: newConfig.latitudeColumn || localConfig.latitudeColumn,
      longitudeColumn: newConfig.longitudeColumn || localConfig.longitudeColumn,
      // Preserve label configuration (critical for labels)
      labelColumn: newConfig.labelColumn || localConfig.labelColumn,
      labelOptions: newConfig.labelOptions || localConfig.labelOptions,
      variable1Column: newConfig.variable1Column || localConfig.variable1Column,
      variable2Column: newConfig.variable2Column || localConfig.variable2Column,
      variable1Text: newConfig.variable1Text || localConfig.variable1Text,
      variable2Text: newConfig.variable2Text || localConfig.variable2Text,
      // Preserve dot transparency
      dotTransparency: newConfig.dotTransparency !== undefined ? newConfig.dotTransparency : localConfig.dotTransparency,
    };

    // CRITICAL FIX: Remove symbol.color for dual-value rendering
    // For 2-variable maps, colors come from colorClassBreakInfos, not symbol.color
    if (preservedConfig.rendererType === 'custom-dual-value' && preservedConfig.symbol) {
      const { color, size, ...symbolWithoutColor } = preservedConfig.symbol;
      preservedConfig.symbol = {
        ...symbolWithoutColor,
        // Keep size for single-value maps, but for dual-value it's overridden by continuous scaling
        // Don't include color at all - it will use colorClassBreakInfos
      };
      console.log('[CustomPointStyleEditor] Removed symbol.color for dual-value rendering');
    }

    console.log('[CustomPointStyleEditor] Config change:', {
      type: preservedConfig.type,
      rendererType: preservedConfig.rendererType,
      hasCustomData: !!preservedConfig.customData?.data?.length,
      valueColumn1: preservedConfig.valueColumn1,
      valueColumn2: preservedConfig.valueColumn2,
      hasColorBreaks: !!preservedConfig.colorClassBreakInfos?.length,
      hasSizeInfos: !!preservedConfig.sizeInfos?.length,
      hasLabelColumn: !!preservedConfig.labelColumn,
      symbolHasColor: !!preservedConfig.symbol?.color,
    });

    setLocalConfig(preservedConfig);
    if (onChange) onChange(preservedConfig);
    // Add preserveLabels flag to prevent label removal during preview
    if (onPreview) onPreview({ ...preservedConfig, preserveLabels: true });
  }, [onChange, onPreview, localConfig]);

  const handleInputChange = (field, value) => {
    const newConfig = { ...localConfig, [field]: value };
    // If value columns change, regenerate breaks
    if (field === 'valueColumn1' && localConfig.customData?.data && value) {
      console.log('[CustomPointStyleEditor] Regenerating color breaks for column:', value);
      const newColorBreaks = generateClassBreaksForPoints(localConfig.customData.data, value, numColorClasses, localConfig.symbol);
      console.log('[CustomPointStyleEditor] Generated color breaks:', newColorBreaks);
      newConfig.colorClassBreakInfos = newColorBreaks;
    }
    if (field === 'valueColumn2' && localConfig.customData?.data && value) {
      console.log('[CustomPointStyleEditor] Regenerating size breaks for column:', value);
      const newSizeBreaks = generateSizeBreaksForPoints(localConfig.customData.data, value, numSizeClasses, minSymSize, maxSymSize);
      console.log('[CustomPointStyleEditor] Generated size breaks:', newSizeBreaks);
      newConfig.sizeInfos = newSizeBreaks;
      newConfig.sizeClassBreakInfos = newSizeBreaks; // Keep both in sync
    }
    // When updating sizeInfos directly, also update sizeClassBreakInfos
    if (field === 'sizeInfos') {
      newConfig.sizeClassBreakInfos = value;
    }
    // When updating sizeClassBreakInfos directly, also update sizeInfos
    if (field === 'sizeClassBreakInfos') {
      newConfig.sizeInfos = value;
    }
    handleFullConfigChange(newConfig);
  };

  // Handle value column selection change - sets pending state requiring legend title
  const handleValueColumnSelect = (columnType, selectedColumn) => {
    if (columnType === 'color') {
      if (selectedColumn !== localConfig.valueColumn1) {
        setPendingColorColumn(selectedColumn);
        setPendingColorLegendTitle('');
      }
    } else if (columnType === 'size') {
      if (selectedColumn !== localConfig.valueColumn2) {
        setPendingSizeColumn(selectedColumn);
        setPendingSizeLegendTitle('');
      }
    }
  };

  // Apply the pending color column change with the legend title
  const applyColorColumnChange = () => {
    if (!pendingColorLegendTitle.trim()) return;

    const newConfig = { ...localConfig, valueColumn1: pendingColorColumn, colorLegendTitle: pendingColorLegendTitle.trim() };
    if (localConfig.customData?.data && pendingColorColumn) {
      console.log('[CustomPointStyleEditor] Applying color column change:', pendingColorColumn);
      const newColorBreaks = generateClassBreaksForPoints(localConfig.customData.data, pendingColorColumn, numColorClasses, localConfig.symbol);
      newConfig.colorClassBreakInfos = newColorBreaks;
    }
    handleFullConfigChange(newConfig);
    setPendingColorColumn(null);
    setPendingColorLegendTitle('');
  };

  // Apply the pending size column change with the legend title
  const applySizeColumnChange = () => {
    if (!pendingSizeLegendTitle.trim()) return;

    const newConfig = { ...localConfig, valueColumn2: pendingSizeColumn, sizeLegendTitle: pendingSizeLegendTitle.trim() };
    if (localConfig.customData?.data && pendingSizeColumn) {
      console.log('[CustomPointStyleEditor] Applying size column change:', pendingSizeColumn);
      const newSizeBreaks = generateSizeBreaksForPoints(localConfig.customData.data, pendingSizeColumn, numSizeClasses, minSymSize, maxSymSize);
      newConfig.sizeInfos = newSizeBreaks;
      newConfig.sizeClassBreakInfos = newSizeBreaks;
    }
    handleFullConfigChange(newConfig);
    setPendingSizeColumn(null);
    setPendingSizeLegendTitle('');
  };

  // Cancel the pending column change
  const cancelColorColumnChange = () => {
    setPendingColorColumn(null);
    setPendingColorLegendTitle('');
  };

  const cancelSizeColumnChange = () => {
    setPendingSizeColumn(null);
    setPendingSizeLegendTitle('');
  };
  
  const handleSymbolChange = (part, field, value) => {
    const newSymbolConfig = { ...(localConfig.symbol || {}) };
    if (part === 'outline') {
      newSymbolConfig.outline = { ...(newSymbolConfig.outline || {}), [field]: value };
    } else {
      newSymbolConfig[part] = value; 
    }
    handleInputChange('symbol', newSymbolConfig);
  };

  const handleNumColorClassesChange = (newNumClasses) => {
    const num = parseInt(newNumClasses, 10);
    if (num > 0 && localConfig.customData?.data && localConfig.valueColumn1) {
      setNumColorClasses(num);
      const newColorBreaks = generateClassBreaksForPoints(localConfig.customData.data, localConfig.valueColumn1, num, localConfig.symbol);
      handleInputChange('colorClassBreakInfos', newColorBreaks);
    }
  };

  const handleNumSizeClassesChange = (newNumClasses) => {
    const num = parseInt(newNumClasses, 10);
    if (num > 0 && localConfig.customData?.data && localConfig.valueColumn2) {
      setNumSizeClasses(num);
      const newSizeBreaks = generateSizeBreaksForPoints(localConfig.customData.data, localConfig.valueColumn2, num, minSymSize, maxSymSize);
      handleInputChange('sizeInfos', newSizeBreaks);
    }
  };
  
  const handleMinMaxSizeChange = (type, value) => {
    const val = parseInt(value, 10);
    let newMin = minSymSize;
    let newMax = maxSymSize;

    if (type === 'min') {
      newMin = Math.max(1, val);
      if (newMin >= newMax) {
        newMin = newMax - 1 >= 1 ? newMax - 1 : 1;
      }
      setMinSymSize(newMin);
    } else {
      newMax = Math.max(1, val);
      if (newMax <= newMin) {
        newMax = newMin + 1;
      }
      setMaxSymSize(newMax);
    }

    const effectiveMinForSymbol = (type === 'min') ? newMin : minSymSize;
    const effectiveMaxForSymbol = (type === 'max') ? newMax : maxSymSize;

    // For continuous sizing, just update min/max in symbol - no discrete breaks needed
    const updatedSymbol = {
      ...(localConfig.symbol || {}),
      minSize: effectiveMinForSymbol,
      maxSize: effectiveMaxForSymbol,
    };

    const newConfig = {
      ...localConfig,
      symbol: updatedSymbol,
      // Keep sizeInfos for backward compatibility but renderer will use continuous sizing
    };

    handleFullConfigChange(newConfig);
  };


  const handleColorBreakChange = (index, newColor) => {
    const newBreaks = [...(localConfig.colorClassBreakInfos || [])];
    if (newBreaks[index] && newBreaks[index].symbol) {
      newBreaks[index].symbol.color = newColor;
      handleInputChange('colorClassBreakInfos', newBreaks);
    }
  };

  const handleSizeBreakChange = (index, newSize) => {
    const newBreaks = [...(localConfig.sizeInfos || [])];
    const parsedSize = parseInt(newSize, 10);
    if (newBreaks[index] && !isNaN(parsedSize) && parsedSize > 0) {
      newBreaks[index].size = parsedSize; // sizeInfos directly stores the size
      handleInputChange('sizeInfos', newBreaks);
    }
  };

  const columns = localConfig.customData?.data?.[0] ? Object.keys(localConfig.customData.data[0]) : [];

  if (!localConfig) return <div>Loading custom config...</div>;

  return (
    <div className="space-y-6 p-1">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white">Custom Point Map Editor</h3>
      
      {/* Column Selection */}
      <div className="space-y-4">
        {/* Value Column 1 (for Color) */}
        <div className="p-4 border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700/30">
          <label htmlFor="valueColumn1" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Value Column 1 (for Color)</label>
          <select
            id="valueColumn1"
            name="valueColumn1"
            value={pendingColorColumn !== null ? pendingColorColumn : (localConfig.valueColumn1 || '')}
            onChange={(e) => handleValueColumnSelect('color', e.target.value)}
            className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white dark:bg-gray-700 dark:border-gray-600 dark:text-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
          >
            <option value="">None</option>
            {columns.map(col => <option key={`v1-${col}`} value={col}>{col}</option>)}
          </select>

          {/* Color Legend Title - appears when column is being changed */}
          {pendingColorColumn !== null && (
            <div className="mt-3 p-3 border border-amber-300 dark:border-amber-600 rounded-md bg-amber-50 dark:bg-amber-900/20">
              <label htmlFor="colorLegendTitle" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Color Legend Title<span className="text-red-500 ml-1">*</span>
              </label>
              <input
                id="colorLegendTitle"
                type="text"
                value={pendingColorLegendTitle}
                onChange={(e) => setPendingColorLegendTitle(e.target.value)}
                placeholder={`e.g., ${pendingColorColumn || 'Variable Name'}`}
                className={`mt-1 block w-full py-2 px-3 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 sm:text-sm ${
                  !pendingColorLegendTitle.trim() ? 'border-red-300 dark:border-red-600' : 'border-gray-300 dark:border-gray-600'
                }`}
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Enter a title for the color legend section.
              </p>
              <div className="mt-3 flex space-x-2">
                <button
                  onClick={applyColorColumnChange}
                  disabled={!pendingColorLegendTitle.trim()}
                  className="flex-1 px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Apply
                </button>
                <button
                  onClick={cancelColorColumnChange}
                  className="px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded-md hover:bg-gray-50 dark:hover:bg-gray-500 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Value Column 2 (for Size) */}
        <div className="p-4 border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700/30">
          <label htmlFor="valueColumn2" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Value Column 2 (for Size)</label>
          <select
            id="valueColumn2"
            name="valueColumn2"
            value={pendingSizeColumn !== null ? pendingSizeColumn : (localConfig.valueColumn2 || '')}
            onChange={(e) => handleValueColumnSelect('size', e.target.value)}
            className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white dark:bg-gray-700 dark:border-gray-600 dark:text-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
          >
            <option value="">None</option>
            {columns.map(col => <option key={`v2-${col}`} value={col}>{col}</option>)}
          </select>

          {/* Size Legend Title - appears when column is being changed */}
          {pendingSizeColumn !== null && (
            <div className="mt-3 p-3 border border-amber-300 dark:border-amber-600 rounded-md bg-amber-50 dark:bg-amber-900/20">
              <label htmlFor="sizeLegendTitle" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Size Legend Title<span className="text-red-500 ml-1">*</span>
              </label>
              <input
                id="sizeLegendTitle"
                type="text"
                value={pendingSizeLegendTitle}
                onChange={(e) => setPendingSizeLegendTitle(e.target.value)}
                placeholder={`e.g., ${pendingSizeColumn || 'Variable Name'}`}
                className={`mt-1 block w-full py-2 px-3 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 sm:text-sm ${
                  !pendingSizeLegendTitle.trim() ? 'border-red-300 dark:border-red-600' : 'border-gray-300 dark:border-gray-600'
                }`}
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Enter a title for the size legend section.
              </p>
              <div className="mt-3 flex space-x-2">
                <button
                  onClick={applySizeColumnChange}
                  disabled={!pendingSizeLegendTitle.trim()}
                  className="flex-1 px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Apply
                </button>
                <button
                  onClick={cancelSizeColumnChange}
                  className="px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded-md hover:bg-gray-50 dark:hover:bg-gray-500 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Base Symbol Style Editor */}
      <div>
        <h4 className="text-md font-medium text-gray-800 dark:text-gray-200 mb-2">Base Symbol Style</h4>
        <div className="grid grid-cols-2 gap-4">
            <div>
                <label htmlFor="symbolStyle" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Shape</label>
                <select id="symbolStyle" value={localConfig.symbol?.style || 'circle'} onChange={(e) => handleSymbolChange('style', null, e.target.value)}
                    className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white dark:bg-gray-700 dark:border-gray-600 dark:text-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm">
                    <option value="circle">Circle</option><option value="square">Square</option><option value="cross">Cross</option><option value="x">X</option><option value="diamond">Diamond</option><option value="triangle">Triangle</option>
                </select>
            </div>
            <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Outline</label>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="noOutline"
                    checked={localConfig.symbol?.outline?.width === 0 || localConfig.symbol?.outline?.width === undefined}
                    onChange={(e) => {
                      if (e.target.checked) {
                        handleSymbolChange('outline', 'width', 0);
                      } else {
                        handleSymbolChange('outline', 'width', 1);
                      }
                    }}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="noOutline" className="ml-2 text-sm text-gray-700 dark:text-gray-300">No Outline</label>
                </div>
            </div>
            {localConfig.symbol?.outline?.width !== 0 && localConfig.symbol?.outline?.width !== undefined && (
              <>
                <div>
                    <label htmlFor="symbolOutlineWidth" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Outline Width (px)</label>
                    <input type="number" id="symbolOutlineWidth" value={localConfig.symbol?.outline?.width || 1} min="0.5" max="5" step="0.5"
                        onChange={(e) => handleSymbolChange('outline', 'width', parseFloat(e.target.value))}
                        className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white dark:bg-gray-700 dark:border-gray-600 dark:text-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" />
                </div>
                <div>
                    <label htmlFor="symbolOutlineColor" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Outline Color</label>
                    <div className="mt-1">
                      <ColorPicker
                        color={localConfig.symbol?.outline?.color || '#FFFFFF'}
                        onChange={(newColor) => handleSymbolChange('outline', 'color', newColor)}
                      />
                    </div>
                </div>
              </>
            )}
        </div>
      </div>

      {/* Dot Transparency */}
      <div className="mt-4">
        <h4 className="text-md font-medium text-gray-800 dark:text-gray-200 mb-2">Dot Transparency</h4>
        <div className="p-3 border rounded-md bg-gray-50 dark:bg-gray-700/50">
          <div className="flex items-center space-x-4">
            <input
              type="range"
              min="0"
              max="1"
              step="0.05"
              value={dotTransparencyInput}
              onChange={(e) => setDotTransparencyInput(parseFloat(e.target.value))}
              className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-600"
            />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300 w-12 text-right">
              {Math.round(dotTransparencyInput * 100)}%
            </span>
          </div>
          <div className="flex items-center space-x-3 mt-3">
            <span className="text-xs text-gray-500 dark:text-gray-400">Preview:</span>
            <div
              className="w-6 h-6 rounded-full border border-gray-400"
              style={{
                backgroundColor: `rgba(59, 130, 246, ${dotTransparencyInput})`,
              }}
            />
            <div
              className="w-4 h-4 rounded-full border border-gray-400"
              style={{
                backgroundColor: `rgba(59, 130, 246, ${dotTransparencyInput})`,
              }}
            />
            <div
              className="w-3 h-3 rounded-full border border-gray-400"
              style={{
                backgroundColor: `rgba(59, 130, 246, ${dotTransparencyInput})`,
              }}
            />
          </div>
        </div>
      </div>

      {/* Color Ramp Editor */}
      {localConfig.valueColumn1 && localConfig.colorClassBreakInfos && (
        <div className="mt-4">
          <div className="flex justify-between items-center mb-2">
            <h4 className="text-md font-medium text-gray-800 dark:text-gray-200">Color Ramp (from '{localConfig.valueColumn1}')</h4>
          </div>
          {/* Number of Breaks Selector for Color */}
          <div className="mb-3">
            <label htmlFor="numColorClasses" className="block text-xs font-medium text-gray-700 dark:text-gray-200 mb-1">
              Number of Breaks
            </label>
            <select
              id="numColorClasses"
              value={numColorClasses}
              onChange={(e) => handleNumColorClassesChange(e.target.value)}
              className="w-full p-1.5 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white border border-gray-300 dark:border-gray-700 rounded"
            >
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => (
                <option key={n} value={n}>{n} {n === 1 ? 'break' : 'breaks'}</option>
              ))}
            </select>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Select the number of color categories for your data.
            </p>
          </div>
          <div className="p-2 border rounded-md bg-gray-50 dark:bg-gray-700/50 max-h-96 overflow-y-auto space-y-2">
            {localConfig.colorClassBreakInfos.map((breakInfo, index) => (
              <div key={`color-edit-${index}`} className="flex items-center space-x-3 p-1 bg-white dark:bg-gray-800 rounded">
                <SymbolPreview symbol={{ ...localConfig.symbol, color: breakInfo.symbol.color, size: 16 }} />
                <span className="text-xs text-gray-700 dark:text-gray-300 flex-1 truncate" title={breakInfo.label}>{breakInfo.label}</span>
                <ColorPicker
                  color={breakInfo.symbol.color}
                  onChange={(newColor) => handleColorBreakChange(index, newColor)}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Size Ramp Editor - Continuous Sizing */}
      {localConfig.valueColumn2 && (
        <div className="mt-4">
          <div className="flex justify-between items-center mb-2">
            <h4 className="text-md font-medium text-gray-800 dark:text-gray-200">Dot Size Range{localConfig.valueColumn2 ? ` (from '${localConfig.valueColumn2}')` : ''}</h4>
          </div>
          {/* Number of Breaks Selector for Size */}
          <div className="mb-3">
            <label htmlFor="numSizeClasses" className="block text-xs font-medium text-gray-700 dark:text-gray-200 mb-1">
              Number of Breaks
            </label>
            <select
              id="numSizeClasses"
              value={numSizeClasses}
              onChange={(e) => handleNumSizeClassesChange(e.target.value)}
              className="w-full p-1.5 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white border border-gray-300 dark:border-gray-700 rounded"
            >
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => (
                <option key={n} value={n}>{n} {n === 1 ? 'break' : 'breaks'}</option>
              ))}
            </select>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Select the number of size categories for your data.
            </p>
          </div>
          {/* Min/Max Size Controls */}
          <div className="mb-3 flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <label htmlFor="minSymSize" className="text-xs text-gray-600 dark:text-gray-400">Min Size:</label>
              <input type="number" id="minSymSize" value={minSymSizeInput} min="1" max={maxSymSize -1}
                onChange={(e) => setMinSymSizeInput(e.target.value)}
                onFocus={(e) => e.target.select()}
                className="w-16 py-1 px-2 border border-gray-300 bg-white dark:bg-gray-700 dark:border-gray-600 dark:text-white rounded-md text-sm"/>
              <span className="text-xs text-gray-500 dark:text-gray-400">px</span>
            </div>
            <div className="flex items-center space-x-2">
              <label htmlFor="maxSymSize" className="text-xs text-gray-600 dark:text-gray-400">Max Size:</label>
              <input type="number" id="maxSymSize" value={maxSymSizeInput} min={minSymSize + 1} max="50"
                onChange={(e) => setMaxSymSizeInput(e.target.value)}
                onFocus={(e) => e.target.select()}
                className="w-16 py-1 px-2 border border-gray-300 bg-white dark:bg-gray-700 dark:border-gray-600 dark:text-white rounded-md text-sm"/>
              <span className="text-xs text-gray-500 dark:text-gray-400">px</span>
            </div>
          </div>

          {/* Size Preview */}
          <div className="bg-gray-100 dark:bg-gray-700/50 p-4 rounded-md">
            <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-3">
              Size Range Preview
            </p>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <div
                  className="rounded-full bg-blue-500 border border-gray-400"
                  style={{
                    width: `${minSymSize}px`,
                    height: `${minSymSize}px`,
                  }}
                />
                <span className="text-xs text-gray-600 dark:text-gray-400">
                  Min ({minSymSize}px)
                </span>
              </div>
              <div className="flex-1 mx-4 border-t border-dashed border-gray-400" />
              <div className="flex items-center space-x-2">
                <span className="text-xs text-gray-600 dark:text-gray-400">
                  Max ({maxSymSize}px)
                </span>
                <div
                  className="rounded-full bg-blue-500 border border-gray-400"
                  style={{
                    width: `${maxSymSize}px`,
                    height: `${maxSymSize}px`,
                  }}
                />
              </div>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 text-center">
              Each point gets a unique size based on its value
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomPointStyleEditor;